import { useEffect, useRef } from 'react';
import type { ScrollView } from 'react-native';
import { useScrollPosition } from '../contexts/useScrollPositionContext';

// Define ViewMode locally to avoid circular dependency
export enum ViewMode {
  LIST = 'list',
  CALENDAR = 'calendar',
}

interface UseCalendarScrollPreservationOptions {
  view: ViewMode;
  scrollKey: string;
  isLoading: boolean;
  hasData: boolean;
}

interface UseCalendarScrollPreservationReturn {
  calendarScrollRef: React.RefObject<ScrollView>;
  handleCalendarScroll: (event: {
    nativeEvent: { contentOffset: { y: number } };
  }) => void;
}

/**
 * Custom hook for preserving scroll position between list and calendar views.
 *
 * @param options Configuration options for scroll preservation
 * @returns Object containing scroll ref and scroll handler
 *
 * @example
 * ```typescript
 * const { calendarScrollRef, handleCalendarScroll } = useCalendarScrollPreservation({
 *   view: ViewMode.CALENDAR,
 *   scrollKey: `/community/${communitySlug}`,
 *   isLoading: isLoadingCommunity,
 *   hasData: Boolean(community),
 * });
 *
 * // Apply to ScrollView component:
 * // ref={calendarScrollRef}
 * // onScroll={handleCalendarScroll}
 * // scrollEventThrottle={100}
 * ```
 */
export function useCalendarScrollPreservation({
  view,
  scrollKey,
  isLoading,
  hasData,
}: UseCalendarScrollPreservationOptions): UseCalendarScrollPreservationReturn {
  const calendarScrollRef = useRef<ScrollView>(null);
  const hasRestoredScrollRef = useRef(false);
  const { getScrollPosition, setScrollPosition } = useScrollPosition();

  // Handle scroll position restoration for calendar view
  useEffect(() => {
    if (
      view === ViewMode.CALENDAR &&
      !hasRestoredScrollRef.current &&
      !isLoading &&
      hasData
    ) {
      const savedPosition = getScrollPosition(scrollKey);
      if (savedPosition && calendarScrollRef.current) {
        // Use a small delay to ensure the content is fully rendered
        const timeoutId = setTimeout(() => {
          if (calendarScrollRef.current) {
            calendarScrollRef.current.scrollTo({
              y: savedPosition.offset,
              animated: false,
            });
          }
          hasRestoredScrollRef.current = true;
        }, 100);

        return () => clearTimeout(timeoutId);
      } else {
        hasRestoredScrollRef.current = true;
      }
    }
  }, [view, isLoading, hasData, getScrollPosition, scrollKey]);

  // Reset scroll restoration flag when view changes
  useEffect(() => {
    hasRestoredScrollRef.current = false;
  }, [view]);

  // Handle scroll position saving for calendar view
  const handleCalendarScroll = (event: {
    nativeEvent: { contentOffset: { y: number } };
  }) => {
    const { y } = event.nativeEvent.contentOffset;
    // Only save if we've scrolled more than 100px to avoid saving minor scrolls
    if (y > 100) {
      setScrollPosition(scrollKey, y);
    }
  };

  return {
    calendarScrollRef,
    handleCalendarScroll,
  };
}
